// Resource mapping utility for AIEmpower

interface Resource {
  id: string;
  title: string;
  startLocationType?: number | null;
  endLocationType?: number | null;
  startLocationLabel?: string | null;
  endLocationLabel?: string | null;
  resourceAddressCoordinates?: {
    latitude: number | null;
    longitude: number | null;
  } | null;
  resourceAddressLabel?: string | null;
  availability?: {
    contracted?: any[];
    available?: any[];
    preferred?: any[];
    unavailable?: any[];
  };
  contractedHours?: any[];
  baseRateAUD?: number | null;
  minimumWorkHoursPerPayroll?: number;
  defaultWorkHoursPerPayroll?: number;
  maximumWorkHourPerPayroll?: number;
  maximumWorkingHoursPerDay?: number;
  currentEmploymentType?: number | null;
  applicableAward?: number | null;
  minimumBreakBetweenShifts?: number | null;
  countPassiveSleepOverAsBreak?: boolean | null;
  payrollHourVariations?: Array<{
    bookableResourceId: string;
    payrollCycleId: string;
    maximumHoursPerPayroll: number | null;
    defaultHoursPerPayroll: number | null;
    minimumHoursPerPayroll: number | null;
    payrollStart: string | null;
    payrollEnd: string | null;
  }>;
  maxHoursPerShift?: number;
  approvedLeaves?: any[];
  characteristics?: Array<{
    name: string;
    ratingValueId?: string;
    ratingName?: string;
    ratingModelName?: string;
    expiryDate?: string | null;
  }>;
  categories?: Array<{ name: string }>;
  siteLocation?: Array<{ name: string }>;
  siteLocations?: Array<{ name?: string } | string>;
  personalNote?: string | null;
}

interface SimplifiedResource {
  id: string;
  name: string;
  availability: {
    contracted: any[];
    available: any[];
    preferred: any[];
    unavailable: any[];
  };
  baseRateAUD: number | null;
  minimumWorkHoursPerPayroll: number | null;
  defaultWorkHoursPerPayroll: number | null;
  maximumWorkHourPerPayroll: number | null;
  maximumWorkingHoursPerDay: number | null;
  currentEmploymentType: number | null;
  applicableAward: number | null;
  minimumBreakBetweenShifts: number | null;
  countPassiveSleepOverAsBreak: boolean | null;
  payrollHourVariations: Array<{
    bookableResourceId: string;
    payrollCycleId: string;
    maximumHoursPerPayroll: number | null;
    defaultHoursPerPayroll: number | null;
    minimumHoursPerPayroll: number | null;
    payrollStart: string | null;
    payrollEnd: string | null;
  }>;
  maxHoursPerShift: number | null;
  approvedLeaves: any[];
  skills: Array<{
    name: string;
    ratingValueId?: string;
    rating?: string;
    ratingModelName?: string;
    expiryDate?: string | null;
  }>;
  categories: string[];
  siteLocations: string[];
  notes: string | null;
}

export function mapResources(resources: Resource[]): { resources: SimplifiedResource[] } {
  const mapped = resources.map((resource) => ({
    id: resource.id,
    name: resource.title,
    startLocationType: resource.startLocationType ?? null,
    endLocationType: resource.endLocationType ?? null,
    startLocationLabel: resource.startLocationLabel ?? null,
    endLocationLabel: resource.endLocationLabel ?? null,
    resourceAddressCoordinates: resource.resourceAddressCoordinates ?? null,
    resourceAddressLabel: resource.resourceAddressLabel ?? null,
    availability: {
      contracted: resource.availability?.contracted ?? [],
      available: resource.availability?.available ?? [],
      preferred: resource.availability?.preferred ?? [],
      unavailable: resource.availability?.unavailable ?? []
    },
    contractedHours: resource.contractedHours ?? resource.availability?.contracted ?? [],
    baseRateAUD: resource.baseRateAUD ?? null,
    minimumWorkHoursPerPayroll: resource.minimumWorkHoursPerPayroll ?? null,
    defaultWorkHoursPerPayroll: resource.defaultWorkHoursPerPayroll ?? null,
    maximumWorkHourPerPayroll: resource.maximumWorkHourPerPayroll ?? null,
    maximumWorkingHoursPerDay: resource.maximumWorkingHoursPerDay ?? null,
    currentEmploymentType: resource.currentEmploymentType ?? null,
    applicableAward: resource.applicableAward ?? null,
    minimumBreakBetweenShifts: resource.minimumBreakBetweenShifts ?? null,
    countPassiveSleepOverAsBreak: resource.countPassiveSleepOverAsBreak ?? null,
    payrollHourVariations: resource.payrollHourVariations ?? [],
    maxHoursPerShift: resource.maxHoursPerShift ?? null,
    approvedLeaves: resource.approvedLeaves ?? [],
    skills: (resource.characteristics ?? []).map((c) => ({
      name: c.name,
      ratingValueId: c.ratingValueId,
      rating: c.ratingName,
      ratingModelName: c.ratingModelName,
      expiryDate: c.expiryDate ?? null
    })),
    categories: (resource.categories ?? []).map((cat) => cat.name),
    siteLocations: (resource.siteLocation ?? []).map((site) => site.name),
    notes: resource.personalNote ?? null
  }));

  return { resources: mapped };
}
